import React from 'react';
import { getLocationDetails } from '../../../../services/dashboard.service';
import { handleAddressInputChange } from '../../../../utils/validations';
import ApplicationField from '../../ApplicationField/ApplicationField';
import ApplicationCountrySelect from '../../ApplicationCountrySelect/ApplicationCountrySelect';
import { ApplicationErrors, ApplicationFieldName, ApplicationShippingAddress } from '../../helpers/validateApplication';
import styles from '../ApplicationForm.module.css';

type ShippingAddressProps = {
  address: ApplicationShippingAddress;
  setAddress: React.Dispatch<React.SetStateAction<ApplicationShippingAddress>>;
  errors: ApplicationErrors;
  clearErrors: (...fields: ApplicationFieldName[]) => void;
};

export default function ShippingAddress({ address, setAddress, errors, clearErrors }: ShippingAddressProps) {
  const handleFetchLocation = async (zipcode: string) => {
    const data = await getLocationDetails(zipcode);
    setAddress((prev) => ({
      ...prev,
      city: data?.city || prev.city,
      state: data?.state || prev.state,
      country: data?.country || prev.country,
    }));
    if (data) clearErrors('city', 'state', 'country');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleAddressInputChange(e, setAddress, handleFetchLocation);
    clearErrors(e.target.name as ApplicationFieldName);
  };

  return (
    <>
      <ApplicationField
        id="addressLineOne"
        name="addressLineOne"
        label="Address"
        required
        placeholder="24th Main Street"
        autoComplete="address-line1"
        value={address.addressLineOne}
        onChange={handleInputChange}
        error={errors.addressLineOne}
      />
      <div className={styles.fieldRow}>
        <ApplicationField
          id="addressLineTwo"
          name="addressLineTwo"
          label="Apartment, suite"
          optional
          placeholder="Apt 4B"
          autoComplete="address-line2"
          value={address.addressLineTwo}
          onChange={handleInputChange}
        />
        <ApplicationField
          id="pincode"
          name="pincode"
          label="Postal code"
          required
          placeholder="10001"
          autoComplete="postal-code"
          value={address.pincode}
          onChange={handleInputChange}
          error={errors.pincode}
        />
      </div>
      <ApplicationField
        id="city"
        name="city"
        label="City"
        required
        placeholder="New York"
        autoComplete="address-level2"
        value={address.city}
        onChange={handleInputChange}
        error={errors.city}
      />
      <ApplicationField
        id="state"
        name="state"
        label="State"
        required
        placeholder="New York"
        autoComplete="address-level1"
        value={address.state}
        onChange={handleInputChange}
        error={errors.state}
      />
      <ApplicationField label="Country" required error={errors.country}>
        <ApplicationCountrySelect
          value={address.country}
          setValue={(country) => {
            setAddress((prev) => ({ ...prev, country }));
            clearErrors('country');
          }}
          placeholder="United States"
          hasError={Boolean(errors.country)}
        />
      </ApplicationField>
    </>
  );
}
